/**
 * v4.8.3 — Tour Completion Card.
 *
 * Cinematic, calm, executive-grade closing surface for the Guided Demo.
 * Replaces the previous "the tour just dissolves at step 8" behavior.
 *
 * Render conditions:
 *   - `useDemo().completed === true`
 *   - Demo Mode remains active throughout — never auto-exited.
 *
 * Interaction surface:
 *   - "Continue Exploring" → dismiss the card; tour overlay closes;
 *     operator stays in Demo Mode on the current route.
 *   - "Restart Guided Demo" → restart from step 0; persona stays loaded.
 *   - "Exit Demo Mode" → dismiss the card, close the overlay, and
 *     deactivate Demo Mode via the backend.
 *
 * Motion discipline:
 *   - Soft 600ms fade-in.
 *   - Slow ambient glow pulse on the Blue Flame logo (4s cycle).
 *   - No particles. No confetti. No celebration vocabulary.
 */
import React from "react";
import { useTranslation } from "react-i18next";
import { Compass, RotateCcw, LogOut } from "lucide-react";
import { useDemo } from "./DemoContext";
import { useDemoMode } from "../demoMode/DemoModeContext";

export const TourCompletionCard = () => {
    const { t } = useTranslation();
    const { completed, persona, dismissCompletion, restart } = useDemo();
    const { set: setDemoMode } = useDemoMode();

    // Escape key → calm dismissal (treat as "Continue Exploring").
    React.useEffect(() => {
        if (!completed) return undefined;
        const onKey = (e) => { if (e.key === "Escape") dismissCompletion(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [completed, dismissCompletion]);

    if (!completed) return null;

    const logoUrl = persona?.logo_url || "/demo/blue_flame.png";
    const personaName = persona?.display_name || "Blue Flame Steakhouse";

    const handleExitDemoMode = async () => {
        // Close the card + overlay first so the transition out of
        // Demo Mode never flashes a stale tour state.
        dismissCompletion();
        await setDemoMode(false);
    };

    return (
        <div
            data-testid="ob-tour-completion-card"
            role="dialog"
            aria-label={t("ob.tour.completion.title")}
            className="fixed inset-0 z-[60] flex items-center justify-center p-6 ob-completion-fade-in"
            style={{
                background: "radial-gradient(ellipse at 50% 50%, rgba(11,16,24,0.92) 0%, rgba(7,11,17,0.96) 70%)",
                backdropFilter: "blur(20px) saturate(125%)",
                WebkitBackdropFilter: "blur(20px) saturate(125%)",
            }}
        >
            <article
                className="rounded-3xl flex flex-col items-center text-center px-10 py-12 ob-completion-card"
                style={{
                    maxWidth: 560,
                    width: "100%",
                    background: "linear-gradient(180deg, rgba(13,18,28,0.95) 0%, rgba(9,13,21,0.96) 100%)",
                    border: "1px solid rgba(16,185,129,0.18)",
                    boxShadow:
                        "0 40px 80px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.025), 0 0 80px rgba(16,185,129,0.10)",
                }}
            >
                {/* Calm logo with slow ambient glow pulse */}
                <div
                    className="relative flex items-center justify-center mb-8 ob-completion-logo-pulse"
                    style={{ width: 88, height: 88 }}
                    data-testid="ob-tour-completion-logo"
                >
                    <div
                        className="absolute inset-0 rounded-full"
                        style={{
                            background:
                                "radial-gradient(circle, rgba(16,185,129,0.22) 0%, rgba(16,185,129,0) 70%)",
                            filter: "blur(8px)",
                        }}
                    />
                    <img
                        src={logoUrl}
                        alt={personaName}
                        className="relative w-full h-full object-contain"
                        style={{ filter: "drop-shadow(0 0 18px rgba(16,185,129,0.25))" }}
                    />
                </div>

                <p
                    className="text-[11px] uppercase tracking-[0.22em] mb-5"
                    style={{
                        color: "#A7F3D0",
                        fontFamily: "Outfit, sans-serif",
                    }}
                    data-testid="ob-tour-completion-eyebrow"
                >
                    {t("ob.tour.completion.eyebrow")}
                </p>

                <h2
                    className="text-3xl sm:text-4xl mb-5"
                    style={{
                        color: "var(--ob-text-primary)",
                        fontFamily: "Outfit, sans-serif",
                        fontWeight: 500,
                        letterSpacing: "-0.01em",
                    }}
                    data-testid="ob-tour-completion-title"
                >
                    {t("ob.tour.completion.title")}
                </h2>

                <p
                    className="text-sm sm:text-base mb-10"
                    style={{
                        color: "var(--ob-text-secondary)",
                        lineHeight: 1.7,
                        maxWidth: "44ch",
                    }}
                    data-testid="ob-tour-completion-subtitle"
                >
                    {t("ob.tour.completion.subtitle")}
                </p>

                <div className="flex flex-col sm:flex-row items-stretch gap-3 w-full max-w-md">
                    <button
                        type="button"
                        onClick={dismissCompletion}
                        data-testid="ob-tour-completion-continue"
                        className="flex-1 inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm transition-transform active:scale-[0.98]"
                        style={{
                            color: "#0B1018",
                            background: "linear-gradient(135deg, #A7F3D0, #6EE7B7)",
                            border: "1px solid rgba(16,185,129,0.40)",
                            fontFamily: "Outfit, sans-serif",
                            fontWeight: 500,
                            boxShadow: "0 6px 24px rgba(16,185,129,0.22)",
                        }}
                    >
                        <Compass size={14} strokeWidth={1.75} />
                        {t("ob.tour.completion.continue")}
                    </button>

                    <button
                        type="button"
                        onClick={restart}
                        data-testid="ob-tour-completion-restart"
                        className="flex-1 inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm transition-colors"
                        style={{
                            color: "var(--ob-text-primary)",
                            background: "rgba(255,255,255,0.04)",
                            border: "1px solid rgba(255,255,255,0.10)",
                            fontFamily: "Outfit, sans-serif",
                        }}
                    >
                        <RotateCcw size={14} strokeWidth={1.5} />
                        {t("ob.tour.completion.restart")}
                    </button>
                </div>

                <button
                    type="button"
                    onClick={handleExitDemoMode}
                    data-testid="ob-tour-completion-exit-demo"
                    className="mt-5 inline-flex items-center gap-1.5 text-xs transition-colors"
                    style={{
                        color: "var(--ob-text-tertiary)",
                        fontFamily: "Outfit, sans-serif",
                    }}
                >
                    <LogOut size={11} strokeWidth={1.5} />
                    {t("ob.tour.completion.exit_demo")}
                </button>
            </article>

            {/* Scoped keyframes — calm, slow, non-distracting. */}
            <style>{`
                @keyframes obCompletionFadeIn {
                    from { opacity: 0; }
                    to   { opacity: 1; }
                }
                @keyframes obCompletionCardEnter {
                    from { opacity: 0; transform: translateY(8px) scale(0.985); }
                    to   { opacity: 1; transform: translateY(0)   scale(1); }
                }
                @keyframes obCompletionLogoPulse {
                    0%, 100% { opacity: 0.92; filter: drop-shadow(0 0 14px rgba(16,185,129,0.18)); }
                    50%      { opacity: 1.00; filter: drop-shadow(0 0 26px rgba(16,185,129,0.32)); }
                }
                .ob-completion-fade-in   { animation: obCompletionFadeIn 600ms ease-out both; }
                .ob-completion-card      { animation: obCompletionCardEnter 720ms cubic-bezier(0.22, 1, 0.36, 1) both; }
                .ob-completion-logo-pulse{ animation: obCompletionLogoPulse 4s ease-in-out infinite; }

                @media (prefers-reduced-motion: reduce) {
                    .ob-completion-fade-in,
                    .ob-completion-card,
                    .ob-completion-logo-pulse { animation: none !important; }
                }
            `}</style>
        </div>
    );
};

export default TourCompletionCard;
